import { useState } from 'react';
import { codeLabel } from '../../utils/codeLabel';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { safePagePlaceholder, staticQueryPolicy } from '../../api/queryPolicies';
import { getAccounts } from '../../api/endpoints/admin';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { AdminAccountResponse } from '../../api/types';
import { DataTable } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { RefreshButton } from '../../components/ui/RefreshButton';
import styles from './AccountsView.module.css';

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function AccountsView() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300, () => setPage(0));

  function handleSearch(v: string) {
    setSearch(v);
  }

  const queryKey = ['admin', 'accounts', page, debouncedSearch] as const;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      getAccounts({
        page,
        size: 25,
        search: debouncedSearch || undefined,
      }),
    ...staticQueryPolicy,
    placeholderData: safePagePlaceholder(queryKey, 3),
  });

  const columns = [
    {
      key: 'id',
      header: 'Account ID',
      width: '120px',
      render: (a: AdminAccountResponse) => (
        <span className={styles.phoneCell}>#{a.id}</span>
      ),
    },
    {
      key: 'phoneNumber',
      header: 'Phone Number',
      width: '180px',
      render: (a: AdminAccountResponse) => (
        <span className={styles.phoneCell}>{a.phoneNumber}</span>
      ),
    },
    {
      key: 'personas',
      header: 'Associated Personas',
      render: (a: AdminAccountResponse) => (
        <div className={styles.personasCell} data-testid={`account-personas-${a.id}`}>
          {a.personas && a.personas.length > 0 ? (
            a.personas.map((p) => (
              <Link
                key={p.id}
                to={`/users/${p.id}`}
                className={styles.personaBadge}
                title={`Open ${p.role} profile (#${p.id})`}
              >
                <span className={styles.personaRole}>{codeLabel('role', p.role)}</span>
                <span className={styles.personaKyc}>({codeLabel('kyc', p.kycStatus)})</span>
              </Link>
            ))
          ) : (
            <span className={styles.emptyPersonas}>{t('accounts.accountsView.noActivePersona')}</span>
          )}
        </div>
      ),
    },
    {
      key: 'deviceCount',
      header: 'Push Devices',
      width: '140px',
      render: (a: AdminAccountResponse) => (
        <span className={styles.deviceCount}>
          {a.deviceCount} {a.deviceCount === 1 ? 'app registered' : 'apps registered'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Registered',
      width: '140px',
      render: (a: AdminAccountResponse) => fmtDate(a.createdAt),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <input
          id="accounts-search"
          className={styles.search}
          type="search"
          placeholder={t('accounts.accountsView.searchPlaceholder')}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <RefreshButton queryKeys={[['admin', 'accounts']]} />
      </div>

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        keyFn={(a) => a.id}
        loading={isLoading}
        error={isError ? 'We couldn’t load accounts' : undefined}
        onRetry={() => refetch()}
        empty="No accounts found."
      />

      {data && data.totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={data.totalPages}
          totalItems={data.totalItems}
          size={data.size}
          onPage={setPage}
        />
      )}
    </div>
  );
}
